import React, { useState, useRef, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../../../constants/colors'
import { OTHER_TEAM_LANGUAGE_OPTIONS, QUICK_SELECT_TEAM_LANGUAGE_OPTIONS } from './languageOptions'
import { teamLanguageOptionTestId } from '../../../../../config/testids'

export interface TeamLanguageSelectorProps {
  value?: string
  onChange: (languageCode: string) => void
  disabled?: boolean
  hasError?: boolean
  ariaLabel?: string
  placeholder?: string
  size?: 'compact' | 'medium'
  className?: string
  /** data-testid for the trigger button (options always get team-language-option-<code>) */
  dataTestId?: string
}

/**
 * TeamLanguageSelector Component
 *
 * Provides an explicit language choice with a prominent English quick-select
 * and the remaining languages listed below in the dropdown.
 */
export const TeamLanguageSelector: React.FC<TeamLanguageSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  ariaLabel,
  placeholder,
  size = 'compact',
  className = '',
  dataTestId
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleSelect = (languageCode: string) => {
    onChange(languageCode)
    setIsOpen(false)
  }

  const selectedCode = value?.toUpperCase()
  const showPlaceholder = !selectedCode
  const isMedium = size === 'medium'

  return (
    <Box
      ref={dropdownRef}
      sx={{
        position: 'relative',
        display: 'inline-block'
      }}
      className={className}
    >
      {/* Trigger Button - styled as button secondary with sun primary border */}
      <Box
        component="button"
        type="button"
        data-testid={dataTestId}
        onClick={handleToggle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label={ariaLabel || t('facilitator.trainingSprint.addParticipants.languageSelector.ariaLabel')}
        disabled={disabled}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: isMedium ? '104px' : '88px',
          appearance: 'none',
          px: isMedium ? 2 : 1.5,
          py: isMedium ? 1.5 : 0.75,
          borderRadius: '8px',
          border: `2px solid ${hasError ? INTERAX_COLORS.redError : INTERAX_COLORS.sun}`,
          background:
            isHovered && !disabled ? INTERAX_COLORS.gradientWhiteSecondaryHover : INTERAX_COLORS.gradientWhiteSecondary,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease',
          '&:disabled': {
            backgroundColor: INTERAX_COLORS.offBlack,
            borderColor: INTERAX_COLORS.white
          }
        }}
      >
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body3,
            fontWeight: 600,
            color: showPlaceholder ? INTERAX_COLORS.thunderGrey : INTERAX_COLORS.white,
            fontSize: isMedium ? '14px' : '12px',
            lineHeight: isMedium ? '20px' : '16px',
            textAlign: 'left',
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {showPlaceholder
            ? placeholder || t('facilitator.trainingSprint.addParticipants.languageSelector.placeholder')
            : selectedCode}
        </Typography>
        <ChevronDownIcon
          style={{
            width: '14px',
            height: '14px',
            color: INTERAX_COLORS.white,
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </Box>

      {/* Dropdown Menu */}
      {isOpen && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: '240px',
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: INTERAX_COLORS.offBlack,
            borderRadius: '8px',
            border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 1000,
            py: 0.5,
            // Hide scrollbar
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none' // IE and Edge
          }}
        >
          <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.thunderGrey,
                fontSize: '11px',
                fontWeight: 700,
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}
            >
              {t('facilitator.trainingSprint.addParticipants.languageSelector.quickSelect')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {QUICK_SELECT_TEAM_LANGUAGE_OPTIONS.map((language) => (
                <Box
                  key={language.code}
                  component="button"
                  type="button"
                  data-testid={teamLanguageOptionTestId(language.code)}
                  onClick={() => handleSelect(language.code)}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    appearance: 'none',
                    px: 1.5,
                    py: 1,
                    borderRadius: '8px',
                    border: `1px solid ${INTERAX_COLORS.sun}`,
                    backgroundColor:
                      value === language.code ? INTERAX_COLORS.heritageBlue40 : INTERAX_COLORS.heritageBlue,
                    color: INTERAX_COLORS.white,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: INTERAX_COLORS.heritageBlue40
                    }
                  }}
                >
                  <Typography
                    sx={{
                      ...INTERAX_TYPOGRAPHY.body3,
                      color: INTERAX_COLORS.white,
                      fontWeight: 600,
                      fontSize: '13px'
                    }}
                  >
                    {language.label}
                  </Typography>
                  <Typography
                    sx={{
                      ...INTERAX_TYPOGRAPHY.body3,
                      color: INTERAX_COLORS.sun,
                      fontWeight: 700,
                      fontSize: '11px'
                    }}
                  >
                    {language.code.toUpperCase()}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              height: '1px',
              backgroundColor: INTERAX_COLORS.thunderGrey,
              opacity: 0.2,
              mx: 1
            }}
          />

          <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.thunderGrey,
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}
            >
              {t('facilitator.trainingSprint.addParticipants.languageSelector.otherLanguages')}
            </Typography>
          </Box>

          {OTHER_TEAM_LANGUAGE_OPTIONS.map((language, index) => (
            <React.Fragment key={language.code}>
              <Box
                component="button"
                type="button"
                data-testid={teamLanguageOptionTestId(language.code)}
                onClick={() => handleSelect(language.code)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  appearance: 'none',
                  border: 'none',
                  px: 2,
                  py: 0.75,
                  cursor: 'pointer',
                  backgroundColor: language.code === value ? INTERAX_COLORS.heritageBlue40 : 'transparent',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: INTERAX_COLORS.heritageBlue40
                  }
                }}
              >
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body3,
                    fontSize: '13px',
                    color: INTERAX_COLORS.white,
                    flex: 1
                  }}
                >
                  {language.label}
                </Typography>
                <Typography
                  sx={{
                    ...INTERAX_TYPOGRAPHY.body3,
                    color: INTERAX_COLORS.thunderGrey,
                    fontWeight: 600,
                    fontSize: '11px',
                    ml: 1
                  }}
                >
                  {language.code.toUpperCase()}
                </Typography>
              </Box>
              {/* Divider between items */}
              {index < OTHER_TEAM_LANGUAGE_OPTIONS.length - 1 && (
                <Box
                  sx={{
                    height: '1px',
                    backgroundColor: INTERAX_COLORS.thunderGrey,
                    opacity: 0.2,
                    mx: 1
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  )
}
